import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Input from "../../components/form/input/InputField";

interface RolePermissionMapping {
  id: string;
  permissionId: string;
  roleId: string;
  createdBy: string;
  createdAt: string;
}

interface Role {
  id: string;
  roleName: string;
}

interface Permission {
  id: string;
  permissionName: string;
}

interface PaginatedResponse {
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
  rolePermissionMappings: RolePermissionMapping[];
}

interface SortConfig {
  key: keyof RolePermissionMapping | '';
  direction: 'asc' | 'desc';
}

export default function RolePermissionMapping() {
  const [mappingsData, setMappings] = useState<RolePermissionMapping[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage, pageSize, sortConfig]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mappingsRes, rolesRes, permissionsRes] = await Promise.all([
        axiosInstance.get<PaginatedResponse>("/RolePermissionMapping/getall", {
          params: {
            search: searchTerm,
            page: currentPage,
            pageSize: pageSize
          }
        }),
        axiosInstance.get("/Role/getall"),
        axiosInstance.get("/Permission/getall")
      ]);

      setMappings(mappingsRes.data?.rolePermissionMappings || []);
      setTotalPages(mappingsRes.data?.totalPages || 1);
      setRoles(rolesRes.data?.roles || []);
      setPermissions(permissionsRes.data?.permissions || []);
    } catch (error) {
      setError("Failed to fetch data");
      console.error("Error fetching data:", error);
      setMappings([]);
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this mapping?")) {
      try {
        await axiosInstance.delete(`/RolePermissionMapping/delete/${id}`);
        fetchData();
      } catch (error) {
        setError("Failed to delete mapping");
        console.error("Error deleting mapping:", error);
      }
    }
  };

  const getRoleName = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    return role?.roleName || roleId;
  };

  const getPermissionName = (permissionId: string) => {
    const permission = permissions.find(p => p.id === permissionId);
    return permission?.permissionName || permissionId;
  };

  const handleSort = (key: keyof RolePermissionMapping) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (key: keyof RolePermissionMapping) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === i
              ? 'bg-brand-500 text-white'
              : 'bg-gray-200 hover:bg-gray-300'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Role Permission Mappings"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to="/role-permission-mapping/add"
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Add New Mapping
        </Link>
      </div>

      <div className="flex justify-between items-center mb-4">
        <Input
          type="text"
          placeholder="Search mappings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-64"
        />
        
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="border rounded-md px-4 py-1"
        >
          <option value="5">5 per page</option>
          <option value="10">10 per page</option>
          <option value="20">20 per page</option>
          <option value="50">50 per page</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : (
        <>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-white/[0.02]">
                <tr>
                  <th 
                    className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                    onClick={() => handleSort('roleId')}
                  >
                    Role {renderSortIcon('roleId')}
                  </th>
                  <th 
                    className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                    onClick={() => handleSort('permissionId')}
                  >
                    Permission {renderSortIcon('permissionId')}
                  </th>
                  <th className="px-5 py-3 text-end text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
                {mappingsData && mappingsData.length > 0 ? (
                  mappingsData.map((mapping) => (
                    <tr key={mapping.id}>
                      <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                        {getRoleName(mapping.roleId)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                        {getPermissionName(mapping.permissionId)}
                      </td>
                      <td className="px-5 py-4 text-end">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                          to={`/permission-role-mapping/edit/${mapping.id}`}
                          className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(mapping.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                      No mappings found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {mappingsData && mappingsData.length > 0 && (
            <div className="mt-4 flex justify-center items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              
              {renderPagination()}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                Next
              </button>
              
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
} 